import type { Report } from '@ofmos/shared/report';
import { Link } from 'react-router';
import type { ObjectMetadata } from '../../data/client';
import { useFields, useReportRun } from '../../data/hooks';
import { Button, EmptyState, ObjectTile, Skeleton } from '../../ui';
import { ReportChart } from './ReportChart';
import { reportChartData } from './reportSeries';
import { formatCount, type ReportGroup, rangeLabel } from './reportsModel';
import { isOverTime, takeawayLine } from './takeaway';

/**
 * THE REPORTS OVERVIEW (wave 5 round 3, owner finding C4-2: "sieht weird aus, nicht
 * sonderlich durchdacht. Warum sollte das so aussehen statt eine Report-Seite, die alle
 * Charts und Tables aufzeigt?").
 *
 * THE CAUSE IT REPLACES. `/reports` was a split view whose detail pane said "No Report
 * Selected" until somebody clicked a name (`ReportsScreen.tsx`, the `reports-nothing-selected`
 * branch). Everything the screen is FOR - a chart with the sentence it proves over it - was
 * one click away per report, and two thirds of the window was an apology for not having been
 * clicked yet. A master list earns its place when the detail is long and the list is how you
 * navigate it; eight named questions with a picture each are the content, not an index to it.
 *
 * APPLE, from the page the charts themselves are drawn from: "you might want to use a small
 * chart to offer glanceable information about an individual item or to provide a snapshot or
 * preview of a larger version of the chart that people can reveal in a different view", and
 * "You can also display brief descriptive text that serves as a headline or summary for a
 * chart, helping people grasp essential information at a glance" (hig:
 * https://developer.apple.com/design/human-interface-guidelines/charting-data). The same page
 * names the precedent this screen is built on: "the Health Trends screen shows small charts
 * that each use a specific visual style to depict a recent trend ... When people choose a
 * chart to reveal all their data in that area, the expanded version uses the same style,
 * colors, marks, and annotations to strengthen the relationship between the versions." The
 * card and the report page therefore render the SAME component (`ReportChart`) at two sizes,
 * which is that continuity in the only form a product can actually keep.
 *
 * THE GROUPING IS THE LIST'S. Reports stay grouped by object under the object's own plural
 * label, because somebody looking for a report thinks "the deal one" (`reportsModel.ts`,
 * `groupReports`). Only the row became a card.
 *
 * ONE RUN PER CARD, AND THAT IS THE PRICE OF THE SCREEN. The list pane deliberately showed
 * the report's own SENTENCE rather than its takeaway, because a takeaway is computed from the
 * numbers and forty rows would have been forty grouped reads before the first paint (ledger
 * E-W4). An overview of charts cannot avoid that read - a chart IS the numbers - so each card
 * asks for its own and TanStack caches the answer under the report's key, which is the same
 * key the report page reads: opening a card shows its chart immediately.
 */

/**
 * THE CARD'S CHART HEIGHT, unchanged from the preview height this product already carried
 * (`ReportChart.tsx`, `ReportPreviewChart`, written for this card and never mounted until
 * now). It is a product number rather than a kit one: neither kit ships a chart master
 * (`/dev/reports-states`, the `chart` origin: "searched the macOS and iOS kit component
 * names; neither provides a chart or series master").
 */
const CARD_CHART_HEIGHT = 120;

/**
 * THE CARD, WHICH IS THE KIT'S GROUPED FORM BOX.
 *
 * kit: Apple macOS 27 UI Kit.sketch / Forms/Example Forms/{Light,Dark}, the Form group - the
 * same box the settings groups and the record page already draw, so the radius and the fill
 * are `--record-group-radius` and `--color-record-group` rather than a second opinion about
 * what a container looks like; those two tokens carry their own kit lines in `tokens.css`.
 *
 * THE WHOLE CARD IS THE TARGET, AND THE NAME IS THE LINK. A link may not contain a focusable
 * element, and the chart is one - `ChartFrame` gives its plot `tabIndex={0}` so the arrow keys
 * can read the values out - so wrapping the card in an anchor would have nested a tab stop
 * inside another. The construction instead is the one the web has for exactly this: the name
 * is the anchor, and the anchor's `::after` covers the card, so a click anywhere on the box
 * opens the report while the accessibility tree still holds ONE link named after it. The
 * chart keeps its own tab stop for reading and takes no pointer events, so nothing inside the
 * card swallows the click.
 */
function ReportCard({
  report,
  object,
}: {
  readonly report: Report;
  readonly object: ObjectMetadata | undefined;
}) {
  const definition = report.definition;
  const run = useReportRun(report.id);
  const fields = useFields(definition.objectId, definition.objectId !== '');
  const loading = run.isPending || fields.isPending;
  const data =
    run.data === undefined || fields.data === undefined
      ? { series: [], points: [], total: null }
      : reportChartData({
          definition,
          result: run.data.result,
          fields: fields.data,
          today: new Date(),
        });

  /**
   * THE SENTENCE IS THE ONE THE REPORT PAGE PRINTS, from the same template and the same
   * numbers (`takeaway.ts`). It refuses rather than guesses - fewer than two comparable
   * buckets is no movement - so a card whose data cannot carry a claim shows the report's
   * own description instead, which is the line the list pane used to show.
   */
  const takeaway = loading
    ? null
    : takeawayLine({
        chart: definition.chart,
        points: data.points,
        format: formatCount,
        rangeLabel: rangeLabel(definition.range),
        overTime: isOverTime(definition),
      });
  const line = takeaway ?? report.description;

  return (
    <li className="reports-card">
      <div className="reports-card-head">
        <ObjectTile icon={object?.icon ?? 'reports'} color={object?.color ?? null} size="sm" />
        <span className="min-w-0">
          <Link to={`/reports/${report.id}`} data-reports-row="row" className="reports-card-name">
            {report.name}
          </Link>
          {line !== null && line !== '' && <span className="reports-card-takeaway">{line}</span>}
        </span>
      </div>
      <div className="reports-card-plot">
        <ReportChart
          definition={definition}
          name={report.name}
          titleHidden
          height={CARD_CHART_HEIGHT}
          loading={loading}
          series={data.series}
          points={data.points}
          total={data.total}
        />
      </div>
    </li>
  );
}

export interface ReportsOverviewProps {
  readonly groups: readonly ReportGroup[];
  readonly loading: boolean;
  readonly canManage: boolean;
  readonly onNew: () => void;
}

export function ReportsOverview({ groups, loading, canManage, onNew }: ReportsOverviewProps) {
  if (loading) {
    /* The placeholder is a CARD in the same grid: a line where the name goes and a block
       where the chart goes, so the page does not reflow around a differently shaped hole. */
    return (
      <div className="reports-overview" aria-busy="true">
        <ul className="reports-overview-grid">
          {['a', 'b'].map((key) => (
            <li key={key} className="reports-card">
              <div className="reports-card-head">
                <Skeleton className="w-40" shape="line" />
              </div>
              <Skeleton shape="block" />
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (groups.length === 0) {
    /* THE EMPTY OVERVIEW (copy deck R4: a noun title, one sentence, one action). */
    return (
      <div className="reports-overview">
        <EmptyState
          icon="search"
          title="No reports"
          description={
            canManage
              ? 'A report is one question about the records, drawn as a chart.'
              : 'Ask an owner or the operator to build one.'
          }
          {...(canManage
            ? {
                action: (
                  <Button variant="secondary" onClick={onNew}>
                    New report…
                  </Button>
                ),
              }
            : {})}
        />
      </div>
    );
  }

  return (
    <div className="reports-overview">
      {groups.map((group) => (
        <section key={group.objectId} aria-label={group.label}>
          <h2 className="reports-overview-heading">{group.label}</h2>
          <ul className="reports-overview-grid">
            {group.reports.map((report) => (
              <ReportCard key={report.id} report={report} object={group.object} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
